/**
 * Color Palette API Service
 *
 * Provides API methods for managing color palettes including:
 * - Fetching available palettes (system and custom)
 * - Getting the active palette for a user/project
 * - Creating/updating/deleting custom palettes
 * - Setting user and organization palette preferences
 */

import { api } from './client';
import type {
  ColorPalette,
  PalettesResponse,
  PaletteResponse,
  ActivePaletteResponse,
  SetPaletteResponse,
  GetPalettesParams,
  GetActivePaletteParams,
  CreatePaletteRequest,
  UpdatePaletteRequest,
  SetUserPaletteRequest,
  SetOrganizationPaletteRequest,
} from '@/types';

const PALETTES_BASE_URL = '/palettes';

export const colorPalettesApi = {
  /**
   * Get all accessible color palettes
   * Includes system palettes + custom palettes for the user
   */
  async getAllPalettes(
    params?: GetPalettesParams
  ): Promise<PalettesResponse> {
    return api.get<{ palettes: ColorPalette[]; total: number }>(
      PALETTES_BASE_URL,
      { params }
    ).then(response => ({
      success: response.success,
      data: response.data!,
    }));
  },

  /**
   * Get all system-provided color palettes
   */
  async getSystemPalettes(): Promise<PalettesResponse> {
    return api.get<{ palettes: ColorPalette[]; total: number }>(
      `${PALETTES_BASE_URL}/system`
    ).then(response => ({
      success: response.success,
      data: response.data!,
    }));
  },

  /**
   * Get a specific color palette by ID
   */
  async getPaletteById(paletteId: string): Promise<PaletteResponse> {
    return api.get<{ palette: ColorPalette }>(
      `${PALETTES_BASE_URL}/${paletteId}`
    ).then(response => ({
      success: response.success,
      data: response.data!,
    }));
  },

  /**
   * Get the active color palette for the current context
   * Uses cascading resolution: User → Project → Organization → Default
   */
  async getActivePalette(
    params?: GetActivePaletteParams
  ): Promise<ActivePaletteResponse> {
    return api.get<{
      palette: ColorPalette;
      metadata: {
        source: 'explicit' | 'user' | 'project' | 'organization' | 'default';
      };
    }>(`${PALETTES_BASE_URL}/active`, { params })
      .then(response => {
        return {
          success: response.success,
          data: response.data!,
        };
      });
  },

  /**
   * Create a new custom color palette
   */
  async createPalette(
    palette: CreatePaletteRequest
  ): Promise<PaletteResponse> {
    return api.post<{ palette: ColorPalette }>(
      PALETTES_BASE_URL,
      palette
    ).then(response => ({
      success: response.success,
      data: response.data!,
    }));
  },

  /**
   * Update an existing color palette
   */
  async updatePalette(
    paletteId: string,
    updates: UpdatePaletteRequest
  ): Promise<PaletteResponse> {
    return api.put<{ palette: ColorPalette }>(
      `${PALETTES_BASE_URL}/${paletteId}`,
      updates
    ).then(response => ({
      success: response.success,
      data: response.data!,
    }));
  },

  /**
   * Delete a custom color palette
   * System palettes cannot be deleted
   */
  async deletePalette(paletteId: string): Promise<SetPaletteResponse> {
    return api.delete<void>(
      `${PALETTES_BASE_URL}/${paletteId}`
    ).then(response => ({
      success: response.success,
      message: 'Palette deleted successfully',
    }));
  },

  /**
   * Set the color palette preference for the current user
   */
  async setUserPalette(
    request: SetUserPaletteRequest
  ): Promise<SetPaletteResponse> {
    console.log('[ColorPalettesAPI] setUserPalette called', {
      request,
      timestamp: new Date().toISOString()
    });

    return api.post<{ paletteId: string }>(
      `${PALETTES_BASE_URL}/user/set`,
      request
    ).then(response => {
      console.log('[ColorPalettesAPI] setUserPalette response', {
        response,
        success: response.success,
        data: response.data,
        timestamp: new Date().toISOString()
      });
      return {
        success: response.success,
        message: 'User palette preference updated',
        data: response.data,
      };
    });
  },

  /**
   * Clear the color palette preference for the current user
   * Falls back to project or organization palette
   */
  async clearUserPalette(): Promise<SetPaletteResponse> {
    return api.delete<void>(
      `${PALETTES_BASE_URL}/user/clear`
    ).then(response => ({
      success: response.success,
      message: 'User palette preference cleared',
    }));
  },

  /**
   * Set the color palette for an organization
   * Requires organization admin permissions
   */
  async setOrganizationPalette(
    request: SetOrganizationPaletteRequest
  ): Promise<SetPaletteResponse> {
    return api.post<{ paletteId: string }>(
      `${PALETTES_BASE_URL}/organization/set`,
      request
    ).then(response => ({
      success: response.success,
      message: 'Organization palette updated',
      data: response.data,
    }));
  },
};
